<template>
    <div class="container-fluid">
        <!-- TITLE -->
        <div class="mt-2 tabName">
            <h3>{{ $t('create_pharmacy') }}</h3>
        </div>
        <!-- CREATE PHARMACY FORM -->
        <div class="row mt-4">
            <div class="col col-sm-8 col-md-6 col-lg-5 col-xl-4">
                <ValidationObserver v-slot="{ handleSubmit, invalid }">
                    <form role="form" @submit.prevent="handleSubmit(submit)">
                        <div class="form-group mb-4">
                            <div class="d-flex flex-row">
                                <div class="d-flex flex-column mr-4">
                                    <span>
                                        *<i> {{ $t('field_required') }}</i>
                                    </span>
                                </div>
                            </div>
                        </div>
                        <!-- NAME -->
                        <div class="form-group">
                            <label for="name">{{ $t('name') }} *</label>
                            <ValidationProvider name="Name" rules="required" v-slot="{ errors }">
                                <input type="text" id="name" class="form-control" v-model="pharmacy.name" />
                                <div class="invalid-feedback" style="display: block">
                                    <span>{{ $t(errors[0]) }}</span>
                                </div>
                            </ValidationProvider>
                        </div>
                        <!-- BUSINESS ID -->
                        <div class="form-group">
                            <label for="business_id">{{ $t('business_id') }} *</label>
                            <ValidationProvider name="Business_id" rules="required" v-slot="{ errors }">
                                <input
                                    type="text"
                                    class="form-control"
                                    id="business_id"
                                    v-model="pharmacy.business_id"
                                />
                                <div class="invalid-feedback" style="display: block">
                                    <span>{{ $t(errors[0]) }}</span>
                                </div>
                            </ValidationProvider>
                        </div>
                        <!-- COUNTRY -->
                        <div class="form-group">
                            <label for="country">{{ $t('country') }} *</label>
                            <ValidationProvider name="Country" rules="required" v-slot="{ errors }">
                                <input type="text" class="form-control" id="country" v-model="pharmacy.country" />
                                <div class="invalid-feedback" style="display: block">
                                    <span>{{ $t(errors[0]) }}</span>
                                </div>
                            </ValidationProvider>
                        </div>
                        <!-- CITY -->
                        <div class="form-group">
                            <label for="city">{{ $t('city') }} *</label>
                            <ValidationProvider name="City" rules="required" v-slot="{ errors }">
                                <input type="text" class="form-control" id="city" v-model="pharmacy.city" />
                                <div class="invalid-feedback" style="display: block">
                                    <span>{{ $t(errors[0]) }}</span>
                                </div>
                            </ValidationProvider>
                        </div>
                        <!-- ZIP -->
                        <div class="form-group">
                            <label for="zip">{{ $t('zip') }}</label>
                            <input type="text" class="form-control" id="zip" v-model="pharmacy.zip" />
                        </div>
                        <!-- STREET ADDRESS -->
                        <div class="form-group">
                            <label for="street">{{ $t('street') }}</label>
                            <input type="text" class="form-control" id="street" v-model="pharmacy.street" />
                        </div>
                        <!-- DESCRIPTION -->
                        <div class="form-group">
                            <label for="description">{{ $t('description') }}</label>
                            <textarea class="form-control" id="description" v-model="pharmacy.description"></textarea>
                        </div>
                        <!-- DRUGSAFE TYPE -->
                        <div class="form-group">
                            <label for="safe_type" style="display: block">{{ $t('safe_type') }} *</label>
                            <select style="display: inline" id="safe_type" class="form-control col-8" v-model="pharmacy.safe_type">
                                <option value="big_no_fridge" selected>{{ $t('big_no_fridge') }}</option>
                                <option value="big_fridge">{{ $t('big_with_fridge') }}</option>
                                <option value="small_no_fridge">{{ $t('small_no_fridge') }}</option>
                                <option value="small_fridge">{{ $t('small_with_fridge') }}</option>
                            </select>
                            <input
                                type="button"
                                v-on:click="() => addModule()"
                                class="btn btn-info col-4"
                                :value="$t('add_module')"
                            />
                        </div>
                        <!-- Drugsafe amount of towers -->
                        <div class="form-group" v-if="pharmacy.additional_modules.length > 0">
                            <label for="additionalModule">{{ $t('additional_modules') }} *</label>
                            <div v-for="(addMod, index) in pharmacy.additional_modules" v-bind:key="index">
                                <select id="additionalModule" class="form-control" v-model="pharmacy.additional_modules[index]">
                                    <option value="big_no_fridge">{{ $t('big_no_fridge') }}</option>
                                    <option value="big_fridge">{{ $t('big_with_fridge') }}</option>
                                    <option value="small_no_fridge">{{ $t('small_no_fridge') }}</option>
                                    <option value="small_fridge">{{ $t('small_with_fridge') }}</option>
                                </select>
                            </div>
                        </div>
                        <!-- BUTTONS (SUBMIT, CANCEL) -->
                        <div class="form-group row">
                            <div class="d-flex ml-3 mr-4">
                                <input
                                    type="submit"
                                    class="btn btn-success mt-2"
                                    :value="$t('save')"
                                    :disabled="invalid || submitClicked"
                                />
                            </div>
                            <div class="d-flex">
                                <input
                                    type="button"
                                    v-on:click="() => $router.back()"
                                    class="btn btn-secondary mt-2"
                                    :value="$t('cancel')"
                                    :disabled="submitClicked"
                                />
                            </div>
                        </div>
                    </form>
                </ValidationObserver>
            </div>
        </div>
    </div>
</template>

<script>
import { createPharmacy, handleAxiosError } from '@/services/axiosService';

export default {
    data() {
        return {
            loaded: false,
            success: false,
            pharmacy: {
                name: null,
                description: null,
                country: null,
                city: null,
                street: null,
                zip: null,
                safe_type: 'big_no_fridge', // default drugsafe type
                additional_modules: []
            },
            submitClicked: false
        };
    },
    created() {
        this.loaded = true;
    },
    methods: {
        // submit form and return to previous page
        async submit() {
            try {
                this.submitClicked = true;
                await createPharmacy(this.pharmacy);
                this.$toastr.s(this.$t('created'));
                this.$router.back();
            } catch (error) {
                handleAxiosError(error);
            }
            this.submitClicked = false;
        },
        addModule() {
            this.pharmacy.additional_modules.push(this.pharmacy.safe_type)
        }
    }
};
</script>
